import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  InputAdornment,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CardHeader,
  CardContent,
  Divider
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { Business } from '../../../types/UserTypes';
import axios from '../../../../services/ApiService';
import { useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { Iconify } from '../../../core/iconify';
import { paths } from '../../../../utils/paths';
import { BVTConstants } from '../../../../utils/BVTConstants';


function AdminCompanyBusinessView() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredBusinesses, setFilteredBusinesses] = useState<Business[]>([]);
  const location = useLocation();
  const companyId = (location.state as { companyId?: number })?.companyId;
  const navigate = useNavigate();

  useEffect(() => {
    fetchBusinesses();
  }, []);

  useEffect(() => {
    filterBusinesses();
  }, [businesses, searchTerm]);

  const fetchBusinesses = async () => {
    setBusinesses([]);
    setLoading(true);
    try {
      const response = await axios.post('/getBusinessesByCompany', companyId);
      
      if (Array.isArray(response.data)) {
        setBusinesses(response.data);
        setFilteredBusinesses(response.data);
      }
    } catch (error) {
      console.error('Error fetching businesses.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filterBusinesses = () => {
    if (!searchTerm) {
      setFilteredBusinesses(businesses);
      return;
    }

    const filtered = businesses.filter(business => 
      business.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      business.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (business.agentCompanyName && business.agentCompanyName.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    setFilteredBusinesses(filtered);
  };

  const handleRowClick = (userId: number) => {
    navigate(paths.admin.agentBusinesses, { 
      state: { userId: userId } 
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case BVTConstants.ACTIVE: return 'success';
      case BVTConstants.INACTIVE: return 'warning';
      default: return 'default';
    }
  };

  return (
    <>
    <Helmet><title>BVT Admin - Clients</title></Helmet>
    <div>
      <Box sx={{ mb: 3 }}>
        <Card>
          <CardHeader 
            title="Clients" 
            subheader="Businesses Assigned to Company"
          />
          <Divider />
          <CardContent>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              mb: 2 
            }}>
              <OutlinedInput
                fullWidth
                placeholder="Search clients..."
                value={searchTerm}
                onChange={handleSearch}
                startAdornment={
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" />
                  </InputAdornment>
                }
              />
            </Box>

            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Legal Name</TableCell>
                    <TableCell>ABN</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredBusinesses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        {loading ? 'Loading clients...' : 'No clients found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBusinesses.map((business) => (
                      <TableRow 
                        key={business.userId}
                        onClick={() => handleRowClick(business.userId)}
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'background-color 0.2s',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell>{business.legalName}</TableCell>
                        <TableCell>{business.businessABN}</TableCell>
                        <TableCell>{business.email}</TableCell>
                        <TableCell>
                          <Chip 
                            label={business.status}
                            color={getStatusColor(business.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(business.createdAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </div>
    <LoadingSpinner loading={loading} />
    </>
  )
}

export default AdminCompanyBusinessView